import { Box, Tab } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useNavbarHeight } from '../../hooks/useNavbarHeight';
import { UserManagementTabs } from '../../types';
import { Snackbar } from '../shared/Snackbar';
import { AllUsersPanel } from './AllUsers';
import { GroupsPanel } from './Groups';
import { RolesPanel } from './Roles';
import { PlatformPanel } from './Platforms';
import { TeamsPanel } from './Teams';
import { Header } from './Header';
import { SearchField } from './SearchField';
import { Container, FloatingLine, ManagementTabs, TabsContainer } from './styles';
import { userManagementSearchPlaceholder, userManagementSearchTerm } from './state';
import { snackbarMessage as userManagementSnackbarMessageAtom } from '@/components/Analysis/state';

const {
  AllUsers: AllUsersTab,
  GROUPS: GroupsTab,
  ROLES: RolesTab,
  PLATFORMS: PlatformsTab,
  TEAMS: TeamsTab,
} = UserManagementTabs;

function a11yProps(tab: UserManagementTabs) {
  return {
    id: `user-tab-${tab}`,
    'aria-controls': `user-tabpanel-${tab}`,
  };
}

export const UserManagement = () => {
  const [currentTab, setCurrentTab] = useState<UserManagementTabs>(AllUsersTab);
  const searchPlaceholder = useAtomValue(userManagementSearchPlaceholder);
  const [searchTerm, setSearchTerm] = useAtom(userManagementSearchTerm);
  const [yOffset, setYOffset] = useState(0);
  const navbarHeight = useNavbarHeight();

  const panelReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (panelReference.current) {
      const rect = panelReference.current.getBoundingClientRect();
      setYOffset(rect.top);
    }
  }, [currentTab]);

  const [snackbarMessage, setSnackbarMessage] = useAtom(userManagementSnackbarMessageAtom);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const { shouldShow, content } = snackbarMessage;

  const handleSelectedTabChange = (newTab: number) => {
    setCurrentTab(newTab);
    setSearchTerm('');
  };

  const visibleTabs = useMemo(() => {
    return [
      {
        label: 'All Users',
        index: AllUsersTab,
        component: <AllUsersPanel />,
        isVisible: true,
        enableSearchField: true,
      },
      {
        label: 'Groups',
        index: GroupsTab,
        component: <GroupsPanel />,
        isVisible: true,
        enableSearchField: true,
      },
      {
        label: 'Roles',
        index: RolesTab,
        component: <RolesPanel />,
        isVisible: true,
        enableSearchField: true,
      },
      {
        label: 'Platforms',
        index: PlatformsTab,
        component: <PlatformPanel />,
        isVisible: true,
        enableSearchField: false,
      },
      {
        label: 'Teams',
        index: TeamsTab,
        component: <TeamsPanel />,
        isVisible: true,
        enableSearchField: false,
      },
    ]
      .filter((tab) => tab.isVisible)
      .map((tab, index) => ({ ...tab, index }));
  }, []);

  const SelectedTab = useMemo(() => {
    return visibleTabs.find((tab) => tab.index === currentTab);
  }, [currentTab, visibleTabs]);

  return (
    <Container height={`calc(100vh - ${navbarHeight}px)`}>
      <Header currentTab={currentTab} />
      <TabsContainer>
        <ManagementTabs
          value={currentTab}
          onChange={(_, newTab) => handleSelectedTabChange(newTab)}
        >
          {visibleTabs.map(({ index, label }) => (
            <Tab key={index} label={label} {...a11yProps(index)} />
          ))}
        </ManagementTabs>
        {SelectedTab?.enableSearchField && (
          <SearchField
            searchPlaceholder={searchPlaceholder}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}
      </TabsContainer>
      <FloatingLine />
      <Box
        ref={panelReference}
        sx={{
          height: `calc(100vh - ${yOffset}px)`,
          overflow: 'hidden',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {SelectedTab?.component}
      </Box>

      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
    </Container>
  );
};
